import { useEffect, useState } from 'react';
import useLocales from 'src/hooks/useLocales';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  Card,
  Table,
  Switch,
  Button,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  FormControlLabel,
} from '@mui/material';

import { useSnackbar } from 'notistack';
import { observer } from 'mobx-react-lite';
import MyDialog from 'src/components/MyDialog';
import StatusDelete from './ElectricMeterDelete';

import ElectricMeterTableRow from './ElectricMeterTableRow';
import ElectricMeterTableToolbar from './ElectricMeterTableToolbar';
import PermissionBasedGuard from 'src/guards/PermissionBasedGuard';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Scrollbar from 'src/components/Scrollbar';
import { TableHeadCustom, TableEmptyRows, TableNoData } from 'src/components/table';
import useSettings from 'src/hooks/useSettings';
import useTable, { getComparator, emptyRows } from 'src/hooks/useTable';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { IElectricMeter } from 'src/@types/Choqorak/Settings/ElectricMeter';
import Page from 'src/components/Page';
import { useStore } from 'src/stores/store';
import ELectricConsumption from './ELectricConsumption';
import { storeIdToLocalStorage } from 'src/Common/storeIdToLocalStorage';

// ----------------------------------------------------------------------

export default observer(function ElectricMeterList() {
  const { ElectricMeterStore, ElectricityPaymentStore } = useStore();
  const { translate } = useLocales();
  const { GetElectricityPaymentHistory } = ElectricityPaymentStore;
  const {
    loadElectricMeter,
    ElectricMeterList,
    ElectricMeterRegistry,
    totalRecord,
    ElectricMeterSearch,
    deleteElectricMeter,
    getElectricMeterFromRegistry,
    setOpenCloseDialog,
    openDialog,
    loadElectricMeterDetail,
    setOpenCloseConsumptionDialog,
    openConsuptionDialog,
  } = ElectricMeterStore;
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangeDense,
    onChangeRowsPerPage,
  } = useTable();

  const { themeStretch, tablePagination, onChangePagination } = useSettings();

  const [filterName, setFilterName] = useState('');

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const [ElectricMeterId, setElectricMeterId] = useState<number>(0);
  const TABLE_HEAD = [
    { id: 'ID', label: `${translate('GeneralFields.Id')}`, align: 'left' },
    {
      id: 'electricMeterSerial',
      label: `${translate('ElectricMeter.ElectricMeterSerial')}`,

      align: 'left',
    },
    {
      id: 'shopCode',
      label: `${translate('Shop.ShopCode')}`,

      align: 'left',
    },
    { id: 'customerName', label: `${translate('Customer.CustomerName')}`, align: 'left' },
    // {
    //   id: 'lastElectricMeterDegree',
    //   label: `${translate('ElectricMeter.LastElectricMeterDegree')}`,
    //   align: 'left',
    // }, {
    //   id: 'hasPamentRecord',
    //   label: `${translate('ElectricMeter.Consumption')}`,
    //   align: 'left',
    // },

    { id: '', label: `${translate('GeneralFields.Action')}` },
  ];
  const handleFilterName = (filterName: string) => {
    setFilterName(filterName);
    setPage(0);
    if (filterName.length > 1) {
      ElectricMeterSearch({
        pageIndex: 0,
        pageSize: tablePagination,
        searchBy: filterName,
      });
    } else if (filterName === '') {
      ElectricMeterSearch({ pageIndex: 0, pageSize: tablePagination });
    }
  };

  const handleOpenConfirm = (id: number) => {
    setOpenCloseDialog();
    setElectricMeterId(id);
  };

  const handleCloseConfirm = () => {
    setOpenCloseDialog();
  };
  const handleEditRow = (id: number) => {
    getElectricMeterFromRegistry(id);
    navigate(PATH_DASHBOARD.ElectricMeter.edit);
  };
  const handleDetail = (id: number) => {
    storeIdToLocalStorage(id, ElectricMeterStore);
    getElectricMeterFromRegistry(id);
    GetElectricityPaymentHistory({ electricmeterId: id });
    navigate(PATH_DASHBOARD.ElectricMeter.detail);
  };
  const handleConsumption = (id: number) => {
    getElectricMeterFromRegistry(id);
    setOpenCloseConsumptionDialog();
  };

  const handleCloseConsumptionConfirm = () => {
    setOpenCloseConsumptionDialog();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    loadElectricMeter({ pageIndex: newPage, pageSize: tablePagination });
  };
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangePagination(event.target.value);
    onChangeRowsPerPage(event);
    let pageZize = parseInt(event.target.value);
    loadElectricMeter({ pageIndex: 0, pageSize: pageZize });
  };

  useEffect(() => {
    onChangeRowsPerPage(tablePagination);
    loadElectricMeter({ pageIndex: 0, pageSize: tablePagination });
  }, [loadElectricMeter, tablePagination]);

  useEffect(() => {
    if (ElectricMeterRegistry.size <= 1) {
      loadElectricMeter({ pageIndex: 0, pageSize: rowsPerPage });
    }
  }, []);

  const dataFiltered = applySortFilter({
    tableData: ElectricMeterList,
    comparator: getComparator(order, orderBy),
    filterName: '',
  });

  const denseHeight = dense ? 52 : 72;

  const isNotFound = !dataFiltered.length && !!filterName;

  return (
    <Page title={translate('ElectricMeter.Title')}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={translate('ElectricMeter.ElectricMeterList')}
          links={[
            { name: `${translate('Department.Dashboard')}`, href: PATH_DASHBOARD.root },

            { name: `${translate('ElectricMeter.ElectricMeterList')}` },
          ]}
          action={
            <>
              <PermissionBasedGuard permissions={['ElectricMeter-Create']}>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  component={RouterLink}
                  to={PATH_DASHBOARD.ElectricMeter.new}
                >
                  {translate('CRUD.Create')}
                </Button>
              </PermissionBasedGuard>
            </>
          }
        />

        <Card>
          <ElectricMeterTableToolbar filterName={filterName} onFilterName={handleFilterName} />

          <Scrollbar>
            <TableContainer sx={{ maxHeight: 600, minWidth: 960, position: 'relative' }}>
              <Table stickyHeader size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={totalRecord}
                  onSort={onSort}
                />

                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <ElectricMeterTableRow
                        key={row.electricMeterId}
                        row={row}
                        index={index}
                        handleDetail={() => handleDetail(row.electricMeterId!)}
                        handleConsumption={() => handleConsumption(row.electricMeterId!)}
                        onDeleteRow={() => handleOpenConfirm(row.electricMeterId!)}
                        onEditRow={() => handleEditRow(row.electricMeterId!)}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(page, rowsPerPage, totalRecord)}
                  />

                  <TableNoData isNotFound={isNotFound} />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100, 150, 200]}
              component="div"
              labelRowsPerPage={translate('Pagination.RowsPerPage')}
              count={totalRecord}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${translate('Pagination.Of')} ${count}`
              }
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handlePageSizeChange}
            />
            <MyDialog
              open={openDialog}
              onClose={handleCloseConfirm}
              title={translate('CRUD.DeleteTitle')}
              size="md"
            >
              <StatusDelete id={ElectricMeterId} />
            </MyDialog>

            <MyDialog
              open={openConsuptionDialog}
              onClose={handleCloseConsumptionConfirm}
              title={translate('ElectricMeter.ElectricConsumption')}
              size="md"
            >
              <ELectricConsumption id={ElectricMeterId} />
            </MyDialog>
            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label={translate('Pagination.Dense')}
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
});

// ----------------------------------------------------------------------

function applySortFilter({
  tableData,
  comparator,
  filterName,
}: {
  tableData: IElectricMeter[];
  comparator: (a: any, b: any) => number;
  filterName: string;
}) {
  const stabilizedThis = tableData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    tableData = tableData.filter(
      (item: Record<string, any>) =>
        item.searchBy.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  return tableData;
}
